import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subscription, take } from 'rxjs';
import { NewsletterApiService } from 'shared/services/newsletter-api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { DialogService } from 'shared/services/dialog.service';
import { FilterRecipientsDialogComponent } from './filter-recipients-dialog/filter-recipients-dialog.component';

export type CampaignStatus = 'draft' | 'pending' | 'sending' | 'stopped' | 'finished';

export interface Campaign {
  _id: string;
  name: string;
  subject: string;
  htmlContent: string;
  startDate: string;
  startTime: string;
  status: CampaignStatus;
  filters: {
    subscriptionStages: number[];
    lastSyncDaysAgo: number;
    hasPaidSubscription: boolean | null;
  };
  stats: {
    totalRecipients: number;
    sent: number;
    opened: number;
    errors: number;
  };
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-campaigns',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatDividerModule,
    MatDialogModule
  ],
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.scss']
})
export class CampaignsComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription[] = [];

  isLoading = true;
  campaigns: Campaign[] = [];

  constructor(
    private _newsletterApi: NewsletterApiService,
    private _snackbar: SnackbarService,
    private _dialog: MatDialog,
    private _dialogService: DialogService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this._loadCampaigns();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  private _loadCampaigns(): void {
    this.isLoading = true;
    const sub = this._newsletterApi.getCampaigns().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.campaigns = response.data;
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this._snackbar.showSnackBar('Błąd ładowania kampanii', 'error');
      }
    });
    this._subscriptions.push(sub);
  }

  createCampaign(): void {
    this._router.navigate(['/admin/campaigns', 'new']);
  }

  editCampaign(campaign: Campaign): void {
    this._router.navigate(['/admin/campaigns', campaign._id]);
  }

  deleteCampaign(campaign: Campaign): void {
    this._dialogService.confirmDialog({
      title: 'Usuń kampanię',
      message: `Czy na pewno chcesz usunąć kampanię "${campaign.name}"? Tej operacji nie można cofnąć.`,
      type: 'warning',
      confirmText: 'Usuń',
      cancelText: 'Anuluj'
    }).subscribe(confirmed => {
      if (confirmed) {
        this._newsletterApi.deleteCampaign(campaign._id).pipe(take(1)).subscribe({
          next: (response: any) => {
            if (response.success) {
              this._snackbar.showSnackBar('Kampania została usunięta', 'success');
              this._loadCampaigns();
            } else {
              this._snackbar.showSnackBar(response.message || 'Błąd usuwania', 'error');
            }
          },
          error: () => {
            this._snackbar.showSnackBar('Błąd usuwania kampanii', 'error');
          }
        });
      }
    });
  }

  openRecipientsDialog(campaign: Campaign): void {
    const dialogRef = this._dialog.open(FilterRecipientsDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { campaign }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._loadCampaigns();
      }
    });
  }

  startCampaign(campaign: Campaign): void {
    this._dialogService.confirmDialog({
      title: 'Uruchom kampanię',
      message: `Czy na pewno chcesz uruchomić kampanię "${campaign.name}"? Wysyłka rozpocznie się zgodnie z ustawionym harmonogramem.`,
      type: 'info',
      confirmText: 'Uruchom',
      cancelText: 'Anuluj'
    }).subscribe(confirmed => {
      if (confirmed) {
        this._updateStatus(campaign, 'pending');
      }
    });
  }

  stopCampaign(campaign: Campaign): void {
    this._dialogService.confirmDialog({
      title: 'Wstrzymaj kampanię',
      message: `Czy na pewno chcesz wstrzymać kampanię "${campaign.name}"? Wysyłka zostanie zatrzymana.`,
      type: 'warning',
      confirmText: 'Wstrzymaj',
      cancelText: 'Anuluj'
    }).subscribe(confirmed => {
      if (confirmed) {
        this._updateStatus(campaign, 'stopped');
      }
    });
  }

  resumeCampaign(campaign: Campaign): void {
    this._updateStatus(campaign, 'pending');
  }

  copyCampaignWithErrors(campaign: Campaign): void {
    this._dialogService.confirmDialog({
      title: 'Kopiuj kampanię do odbiorców z błędami',
      message: `Utworzy nową kampanię z tymi samymi ustawieniami i ${campaign.stats.errors} odbiorcami, którzy mieli błędy wysyłki.`,
      type: 'info',
      confirmText: 'Kopiuj',
      cancelText: 'Anuluj'
    }).subscribe(confirmed => {
      if (confirmed) {
        this._newsletterApi.copyCampaignWithErrors(campaign._id).pipe(take(1)).subscribe({
          next: (response: any) => {
            if (response.success) {
              this._snackbar.showSnackBar(response.message || 'Kampania skopiowana', 'success');
              this._loadCampaigns();
            } else {
              this._snackbar.showSnackBar(response.message || 'Błąd kopiowania', 'error');
            }
          },
          error: () => {
            this._snackbar.showSnackBar('Błąd kopiowania kampanii', 'error');
          }
        });
      }
    });
  }

  private _updateStatus(campaign: Campaign, status: CampaignStatus): void {
    this._newsletterApi.updateCampaignStatus(campaign._id, status).pipe(take(1)).subscribe({
      next: (response: any) => {
        if (response.success) {
          this._snackbar.showSnackBar(response.message || 'Status zaktualizowany', 'success');
          this._loadCampaigns();
        } else {
          this._snackbar.showSnackBar(response.message || 'Błąd', 'error');
        }
      },
      error: () => {
        this._snackbar.showSnackBar('Błąd aktualizacji statusu', 'error');
      }
    });
  }

  // Helpers
  getStatusLabel(status: CampaignStatus): string {
    const labels: Record<CampaignStatus, string> = {
      draft: 'Szkic',
      pending: 'Zaplanowana',
      sending: 'Wysyłanie',
      stopped: 'Wstrzymana',
      finished: 'Zakończona'
    };
    return labels[status] || status;
  }

  getStatusClass(status: CampaignStatus): string {
    const classes: Record<CampaignStatus, string> = {
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      sending: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      stopped: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      finished: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
    };
    return classes[status] || '';
  }

  getStatusIcon(status: CampaignStatus): string {
    const icons: Record<CampaignStatus, string> = {
      draft: 'tabler_outline:file-text',
      pending: 'tabler_outline:clock',
      sending: 'tabler_outline:send',
      stopped: 'tabler_outline:player-pause',
      finished: 'tabler_outline:check'
    };
    return icons[status] || 'tabler_outline:mail';
  }

  getSentProgress(campaign: Campaign): number {
    if (campaign.stats.totalRecipients === 0) return 0;
    const processed = campaign.stats.sent + campaign.stats.errors;
    return Math.round((processed / campaign.stats.totalRecipients) * 100);
  }

  getOpenedProgress(campaign: Campaign): number {
    if (campaign.stats.sent === 0) return 0;
    return Math.round((campaign.stats.opened / campaign.stats.sent) * 100);
  }

  /**
   * Zwraca kolor dla progresu (od żółtego do zielonego)
   * 0% = żółty (#eab308), 100% = zielony (#22c55e)
   */
  getProgressColor(percent: number): string {
    // Clamp between 0-100
    const p = Math.max(0, Math.min(100, percent));
    
    // Yellow: rgb(234, 179, 8) -> Green: rgb(34, 197, 94)
    const r = Math.round(234 + (34 - 234) * (p / 100));
    const g = Math.round(179 + (197 - 179) * (p / 100));
    const b = Math.round(8 + (94 - 8) * (p / 100));
    
    return `rgb(${r}, ${g}, ${b})`;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}
